import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { SpinnerBlock } from "../common/Spinner";
import * as authSelectors from "./../auth/authSelectors";
import VenueUpsertForm from "./VenueUpsertForm";
import * as _ from "lodash";
import { withTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import VenueShopNav from "./VenueShopNav";
import * as tagActions from "../tag/tagActions";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { routes } from "../../appRoutingHelpers";
import {
  useCreateRestaurant,
  useDeleteRestaurantImage,
  useGetRestaurant,
  useUpdateRestaurant,
  useUploadRestaurantImage,
  useUploadRestaurantLogo,
} from "./service/venue-api";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";

const VenueUpsert: React.FC<{}> = (props) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string | undefined }>();
  const isEdit = !!id;
  const t = (props as any).t;
  const navigate = useNavigate();
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);
  const hasAccessToShops = useSelector(authSelectors.hasAccessToShops);
  const {
    data: restaurant,
    isLoading: isLoadingRestaurant,
    error: getRestaurantError,
  } = useGetRestaurant(organization, id);

  const { mutateAsync: uploadRestaurantImage } = useUploadRestaurantImage();
  const { mutateAsync: uploadRestaurantLogo } = useUploadRestaurantLogo();
  const { mutateAsync: createRestaurant } = useCreateRestaurant();
  const { mutateAsync: updateRestaurant } = useUpdateRestaurant(id);
  const { mutateAsync: deleteRestaurantImage } = useDeleteRestaurantImage(id);

  React.useEffect(() => {
    dispatch(tagActions.getTagsForOrganization(organization));
  }, [dispatch, organization]);

  const onInsertSubmit = async (
    values: any,
    filesToUpload: File[],
    venueLogoFile: File | undefined
  ) => {
    setIsLoading(true);
    try {
      const id = await createRestaurant({
        data: values,
        organizationKeyName: organization,
      });
      for (const file of filesToUpload) {
        await uploadRestaurantImage({
          file,
          id,
          organizationKeyName: organization,
        });
      }
      if (venueLogoFile) {
        await uploadRestaurantLogo({
          file: venueLogoFile,
          id,
          organizationKeyName: organization,
        });
      }
      toastr.success("Success", "Restaurant created successfully");
      navigate(routes.venueList(organization));
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    } finally {
      setIsLoading(false);
    }
  };

  const onEditSubmit = async (
    values: any,
    filesToUpload: File[],
    venueLogoFile: File | undefined
  ) => {
    if (!id) {
      throw new Error("No restaurant id");
    }
    setIsLoading(true);
    try {
      await updateRestaurant({
        data: values,
        organizationKeyName: organization,
        id,
      });
      for (const file of filesToUpload) {
        await uploadRestaurantImage({
          file,
          id,
          organizationKeyName: organization,
        });
      }
      if (venueLogoFile) {
        await uploadRestaurantLogo({
          file: venueLogoFile,
          id,
          organizationKeyName: organization,
        });
      }
      toastr.success("Success", "Restaurant updated successfully");
      navigate(routes.venueList(organization));
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    } finally {
      setIsLoading(false);
    }
  };

  const onDeleteImage = async (imageSrc: string): Promise<void> => {
    const venueId = id;
    if (!venueId) {
      throw new Error("No restaurant id");
    }

    if (_.size(restaurant?.images) === 1) {
      alert(t("venue_upsert.confirm_delete_deny_last_image"));
      return;
    }

    if (!window.confirm(t("venue_upsert.confirm_delete"))) {
      return;
    }
    try {
      await deleteRestaurantImage({
        imageUrl: imageSrc,
        id: venueId,
        organizationKeyName: organization,
      });
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  useHandleFetchError(getRestaurantError);

  return (
    <>
      <div className="container mt-5">
        <GoBackLinkButton
          to={routes.venueList(organization)}
          text="Back to all restaurants"
        />
        {hasAccessToShops && (
          <VenueShopNav
            organization={organization}
            venueId={isEdit ? restaurant?.id : undefined}
            shopId={isEdit ? restaurant?.shopId : undefined}
          />
        )}

        {isLoadingRestaurant || (isEdit && !restaurant) ? (
          <SpinnerBlock />
        ) : (
          <>
            <div className="row">
              <div className="col-12">
                <VenueUpsertForm
                  isLoading={isLoading}
                  onSubmit={isEdit ? onEditSubmit : onInsertSubmit}
                  onDeleteImage={onDeleteImage}
                  venue={isEdit ? restaurant || {} : {}}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default withTranslation()(VenueUpsert);
